import { ArrowLeft, CheckCircle2, Clock3, Download, LockKeyhole, UnlockKeyhole, WifiOff } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ExerciseContent from '../components/training/ExerciseContent';
import { useAsyncQuery } from '../hooks/useAsyncQuery';
import { useI18n } from '../i18n';
import { getTrainingExerciseDetailApi } from '../lib/trainingContentApi';
import type { Actor, Step } from '../drilleditor/types';
import { getOfflineTrainingExercise, saveOfflineTrainingExercise } from '../lib/offlineTrainingStore';
import TrainingShareButton from '../components/training/TrainingShareButton';

type TrainingExerciseDetailRow = {
  id: string;
  drill_id?: string | null;
  security_level: 'public' | 'club' | 'individual' | null;
  title: string | null;
  duration: number | null;
  tags: unknown;
  categories: unknown;
  introduction: string | null;
  actors: unknown;
  timeline: unknown;
  notes: unknown;
  created_by_name?: string | null;
  _offline?: boolean;
};

const asStrings = (value: unknown): string[] => (
  Array.isArray(value) ? value.map(item => String(item)).filter(Boolean) : []
);

type TrainingExerciseDetailProps = {
  exerciseIdentifier?: string;
  offlineOnly?: boolean;
  backTo?: string;
  flushMobileHeader?: boolean;
};

const fromOfflineRecord = (record: Awaited<ReturnType<typeof getOfflineTrainingExercise>>): TrainingExerciseDetailRow | null => record ? ({
  id: record.exerciseId || record.id,
  drill_id: record.drill.id,
  security_level: 'public',
  title: record.drill.title,
  duration: record.drill.duration ?? null,
  tags: record.drill.tags,
  categories: record.drill.categories,
  introduction: record.drill.introduction ?? null,
  actors: record.drill.actors,
  timeline: record.drill.timeline,
  notes: record.drill.notes,
  _offline: true,
}) : null;

export default function TrainingExerciseDetail({ exerciseIdentifier, offlineOnly = false, backTo = '/training-practices', flushMobileHeader = false }: TrainingExerciseDetailProps = {}) {
  const { id: routeId } = useParams<{ id: string }>();
  const id = exerciseIdentifier || routeId;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { t } = useI18n();
  const [offlineSaveState, setOfflineSaveState] = useState<'idle' | 'saved' | 'error'>('idle');
  const { data: exercise, loading, error } = useAsyncQuery<TrainingExerciseDetailRow | null>(
    async () => {
      if (!id) return { data: null, error: new Error('Missing exercise id') };
      if (offlineOnly) {
        const offline = fromOfflineRecord(await getOfflineTrainingExercise(id));
        return { data: offline, error: offline ? null : new Error('Offline exercise not found') };
      }
      try {
        const row = await getTrainingExerciseDetailApi(id) as TrainingExerciseDetailRow | null;
        return { data: row, error: null };
      } catch (requestError) {
        const offline = fromOfflineRecord(await getOfflineTrainingExercise(id));
        if (offline) return { data: offline, error: null };
        return { data: null, error: requestError };
      }
    },
    [id, offlineOnly],
    null,
    'TrainingExerciseDetail:exercise'
  );

  const actors = useMemo(
    () => (Array.isArray(exercise?.actors) ? exercise.actors as Actor[] : []),
    [exercise?.actors]
  );
  const timeline = useMemo(
    () => (Array.isArray(exercise?.timeline) ? exercise.timeline as Step[] : []),
    [exercise?.timeline]
  );
  const tags = useMemo(() => asStrings(exercise?.tags), [exercise?.tags]);
  const categories = useMemo(() => asStrings(exercise?.categories), [exercise?.categories]);
  const notes = useMemo(() => asStrings(exercise?.notes), [exercise?.notes]);
  const securityLevel = exercise?.security_level || 'public';

  useEffect(() => setOfflineSaveState('idle'), [exercise?.id]);

  const saveOffline = async () => {
    if (!exercise) return;
    try {
      await saveOfflineTrainingExercise(exercise);
      setOfflineSaveState('saved');
    } catch {
      setOfflineSaveState('error');
    }
  };

  return (
    <div className={`ui-screen ${flushMobileHeader ? 'space-y-0 px-0 py-0 sm:space-y-4 sm:px-4 sm:py-4' : 'space-y-4'}`}>
      <button
        type="button"
        onClick={() => navigate(params.get('library') === 'club' && backTo === '/training-practices' ? '/club-exercises' : backTo)}
        className={`inline-flex items-center gap-1 text-sm text-gray-500 transition-colors hover:text-gray-900 ${flushMobileHeader ? 'min-h-12 w-full border-b border-gray-200 bg-white px-4 sm:min-h-0 sm:w-auto sm:border-0 sm:bg-transparent sm:px-0' : 'min-h-11 sm:min-h-0'}`}
      >
        <ArrowLeft size={16} />
        {t('training_exercises_detail_back')}
      </button>

      {loading && <div className="border-y border-gray-200 bg-white px-6 py-8 text-sm text-gray-500">{t('training_exercises_loading')}</div>}
      {!loading && (error || !exercise) && <div className="border-y border-gray-200 bg-white px-6 py-8 text-sm text-error-600">{t('training_exercises_detail_error')}</div>}

      {!loading && exercise && (
        <article className={`${flushMobileHeader ? 'border-b sm:border-y' : 'border-y'} border-gray-200 bg-white`}>
          <header className="flex flex-wrap items-start justify-between gap-4 border-b border-gray-200 px-4 py-4 sm:px-6 sm:py-5">
            <div className="min-w-0">
              <div className="mb-2 flex flex-wrap items-center gap-2 text-xs font-medium">
                <span className={`inline-flex items-center gap-1 ${securityLevel === 'public' ? 'text-orange-600' : 'text-gray-600'}`}>
                  {securityLevel === 'public' ? <UnlockKeyhole size={13} /> : <LockKeyhole size={13} />}
                  {t(`training_exercises_security_${securityLevel}`)}
                </span>
                {exercise.created_by_name && <span className="text-gray-500">{exercise.created_by_name}</span>}
              </div>
              <h1 className="ui-page-title">{exercise.title?.trim() || t('training_exercises_untitled')}</h1>
              {exercise.introduction && <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-600">{exercise.introduction}</p>}
              {exercise._offline && <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700"><WifiOff size={14} /> Offline exemplaar</p>}
            </div>
            <div className="flex w-full items-center gap-3 sm:w-auto">
              {exercise.duration !== null && <span className="inline-flex items-center gap-2 text-sm font-medium text-gray-600"><Clock3 size={16} />{exercise.duration} min</span>}
              {!exercise._offline && <button type="button" onClick={() => void saveOffline()} className="ml-auto inline-flex min-h-11 items-center justify-center gap-2 border border-gray-200 px-3 text-sm font-semibold text-gray-700 hover:border-orange-300 hover:text-orange-700 sm:min-h-0"><Download size={16} /> Offline bewaren</button>}
              {!exercise._offline && <TrainingShareButton resourceType="exercise" resourceId={exercise.id} title={exercise.title?.trim() || t('training_exercises_untitled')} compact />}
            </div>
            {offlineSaveState === 'saved' && <p className="w-full text-xs font-semibold text-emerald-700"><CheckCircle2 size={14} className="mr-1 inline" />Op dit apparaat opgeslagen.</p>}
            {offlineSaveState === 'error' && <p className="w-full text-xs font-semibold text-error-700">Offline opslaan is mislukt.</p>}
          </header>

          <ExerciseContent id={exercise.drill_id || exercise.id} title={exercise.title?.trim() || t('training_exercises_untitled')} actors={actors} timeline={timeline} tags={tags} categories={categories} notes={notes} />
        </article>
      )}
    </div>
  );
}
